interface ApiAuthSession {
  expireAt: string;
}

class SessionManager {
  private static instance: SessionManager;

  private gateway: string = import.meta.env.VITE_API_GATEWAY_URL;
  private _expireAt?: Date;
  private _expireTimeout?: NodeJS.Timeout;
  private _isUpdating: Promise<void> = Promise.resolve();

  private constructor() {}

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  public setGateway(gateway: string): void {
    this.gateway = gateway;
  }

  public getIsUpdating(): Promise<void> {
    return this._isUpdating;
  }

  public async create(googleJWT: string): Promise<void> {
    let resolveUpdating!: () => void;
    this._isUpdating = new Promise<void>(resolve => {
      resolveUpdating = resolve;
    });

    try {
      this.clearSessionState();
      const response = await fetch(`${this.gateway}/api/auth/session`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${googleJWT}` },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to create session');
      }

      const session: ApiAuthSession = await response.json();
      this.update(session);
    } finally {
      resolveUpdating();
    }
  }

  public async destroy(): Promise<void> {
    this.clearSessionState();

    try {
      await fetch(`${this.gateway}/api/auth/session`, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch {
      // Ignore errors when the session is already invalidated
    }
  }

  public reset(): void {
    this.clearSessionState();
  }

  public async sync(): Promise<boolean> {
    try {
      await this.refreshSession();
      return true;
    } catch {
      return false;
    }
  }

  private async refreshSession(): Promise<void> {
    let resolveUpdating!: () => void;
    this._isUpdating = new Promise<void>(resolve => {
      resolveUpdating = resolve;
    });

    try {
      const response = await fetch(`${this.gateway}/api/auth/session`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to refresh session');
      }

      const session: ApiAuthSession = await response.json();
      this.update(session);
    } catch (error) {
      this.clearSessionState();
      throw error;
    } finally {
      resolveUpdating();
    }
  }

  private update({ expireAt }: ApiAuthSession): void {
    if (!expireAt) {
      throw new Error(`Can't update session`);
    }

    this.clearSessionTimeout();
    this._expireAt = new Date(expireAt);
    const timeout = this._expireAt.getTime() - Date.now() - 30_000;

    if (timeout > 0) {
      this._expireTimeout = setTimeout(() => {
        void this.refreshSession().catch(() => undefined);
      }, Math.min(timeout, 3_600_000));
    }
  }

  private clearSessionTimeout(): void {
    if (this._expireTimeout) {
      clearTimeout(this._expireTimeout);
      this._expireTimeout = undefined;
    }
  }

  private clearSessionState(): void {
    this.clearSessionTimeout();
    this._expireAt = undefined;
  }
}

export const sessionManager = SessionManager.getInstance();
